import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../api/api';
import {
    AlertTriangle,
    CheckCircle2,
    Signal,
    Building2,
    ShieldAlert,
    Info,
    RefreshCcw,
    EyeOff,
    SearchX,
    Filter,
    X,
    LayoutGrid,
    ExternalLink,
    ShieldCheck,
    History,
    Scale,
    Layers
} from 'lucide-react';
import { SnackbarContext } from '../App';
import PageShell from './ui/PageShell';
import PageHeader from './ui/PageHeader';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import EmptyState from './ui/EmptyState';
import { 
    Card, 
    CardHeader, 
    CardTitle, 
    CardContent, 
    CardDescription 
} from './ui/card';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./ui/select";
import { cn } from '../lib/utils';

// Helper for qualitative scoring impact
const getScoringImpact = (severity) => {
    switch(severity) {
        case 'HIGH': return { weight: '+20 pts', label: 'High Impact', color: 'text-rose-500' };
        case 'MEDIUM': return { weight: '+10 pts', label: 'Medium Impact', color: 'text-amber-500' };
        case 'LOW': return { weight: '+5 pts', label: 'Low Impact', color: 'text-blue-500' };
        default: return { weight: '?', label: 'Unknown', color: 'text-muted-foreground' };
    }
};

// Memoized Queue Card for performance
const QueueItem = React.memo(({ issue, isSelected, onClick, getSeverityStyles, getIssueReasonSnippet }) => (
    <div 
        onClick={onClick}
        className={cn(
            "group p-4 rounded-2xl border cursor-pointer transition-all duration-200",
            isSelected ? "border-primary bg-primary/5 shadow-md ring-1 ring-primary/20" : "border-border hover:bg-muted/50"
        )}
    >
        <div className="flex justify-between items-start mb-2">
            <Badge variant="outline" className={cn("text-[8px] font-black uppercase px-1.5 py-0.5 rounded-md", getSeverityStyles(issue.severity))}>
                {issue.severity}
            </Badge>
            <span className="text-[10px] font-bold text-muted-foreground opacity-50">{getIssueReasonSnippet(issue)}</span>
        </div>
        <div className="font-black text-xs text-foreground uppercase tracking-tight truncate mb-1">{issue.type.replace(/_/g, ' ')}</div>
        <div className="text-[10px] font-bold text-muted-foreground truncate">{issue.companyMasterId?.name}</div>
    </div>
));

const SignalGovernanceDashboard = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [issues, setIssues] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedIssue, setSelectedIssue] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    
    // Deep Hydration State (Sprint 7)
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState(false);
    
    // Action Workflow State
    const [activeAction, setActiveAction] = useState(null); // 'SUPPRESS' | 'RESOLVE'
    const [actionRationale, setActionRationale] = useState('');
    const [confirmSuppression, setConfirmSuppression] = useState(false);
    
    const showSnackbar = useContext(SnackbarContext);

    // Filter state derived from URL
    const currentSeverity = searchParams.get('severity') || 'ALL';
    const currentType = searchParams.get('type') || 'ALL';
    const currentIssueId = searchParams.get('issueId');

    const updateFilters = useCallback((updates) => {
        const newParams = new URLSearchParams(searchParams);
        Object.entries(updates).forEach(([key, value]) => {
            if (value && value !== 'ALL') {
                newParams.set(key, value);
            } else {
                newParams.delete(key);
            }
        });
        setSearchParams(newParams);
    }, [searchParams, setSearchParams]);

    const loadIssues = useCallback(async (autoSelectId = null) => {
        setLoading(true);
        try {
            const params = {};
            if (currentSeverity !== 'ALL') params.severity = currentSeverity;
            if (currentType !== 'ALL') params.type = currentType;
            
            const res = await api.get('/api/ops/signals/governance/issues', { params });
            const data = res.data.data;
            setIssues(data);

            // Handle selection logic (without population here)
            const targetId = autoSelectId || currentIssueId;
            if (targetId) {
                const found = data.find(i => i._id === targetId);
                if (found) {
                    if (targetId !== currentIssueId) updateFilters({ issueId: found._id });
                } else if (autoSelectId) {
                    updateFilters({ issueId: null });
                }
            }
        } catch (err) {
            showSnackbar('Failed to load governance queue', 'error');
        } finally {
            setLoading(false);
        }
    }, [currentSeverity, currentType, currentIssueId, showSnackbar, updateFilters]);

    // Deep Hydration Effect (Sprint 7)
    useEffect(() => {
        const fetchDetail = async () => {
            if (!currentIssueId) {
                setSelectedIssue(null);
                return;
            }
            
            setDetailLoading(true);
            setDetailError(false);
            try {
                const res = await api.get(`/api/ops/signals/governance/issues/${currentIssueId}`);
                setSelectedIssue(res.data.data);
            } catch (err) {
                if (err.response?.status === 404) {
                    showSnackbar('Governance issue no longer available', 'warning');
                    updateFilters({ issueId: null });
                } else {
                    setDetailError(true);
                    showSnackbar('Failed to hydrate deep evidence', 'error');
                }
            } finally {
                setDetailLoading(false);
            }
        };

        fetchDetail();
    }, [currentIssueId, showSnackbar, updateFilters]);

    useEffect(() => {
        loadIssues();
    }, [currentSeverity, currentType, loadIssues]);

    const handleSelectIssue = (issue) => {
        setConfirmSuppression(false);
        setActiveAction(null);
        setActionRationale('');
        updateFilters({ issueId: issue?._id });
    };

    const getNextIssueId = (currentId) => {
        const currentIndex = issues.findIndex(i => i._id === currentId);
        if (currentIndex === -1) return null;
        if (currentIndex < issues.length - 1) return issues[currentIndex + 1]._id;
        if (currentIndex > 0) return issues[currentIndex - 1]._id;
        return null;
    };

    const handleResolve = async (issueId, action, notes = '') => {
        setActionLoading(true);
        const nextId = getNextIssueId(issueId);
        try {
            await api.post('/api/ops/signals/governance/resolve', { issueId, action, notes: notes || actionRationale });
            showSnackbar(`Issue ${action === 'DISMISS' ? 'dismissed' : 'resolved'} successfully.`, 'success');
            
            // Surgical local removal (Sprint 7)
            setIssues(prev => prev.filter(i => i._id !== issueId));
            updateFilters({ issueId: nextId });
            
            setActiveAction(null);
            setActionRationale('');
        } catch (err) {
            showSnackbar('Action failed', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleSuppress = async (signalId) => {
        if (!confirmSuppression) {
            if (!actionRationale || actionRationale.trim().length < 10) {
                showSnackbar('Rationale too short', 'warning');
                return;
            }
            setConfirmSuppression(true);
            return;
        }
        setActionLoading(true);
        const issueId = selectedIssue._id;
        const nextId = getNextIssueId(issueId);
        try {
            await api.post(`/api/ops/signals/${signalId}/suppress`, { reason: actionRationale });
            showSnackbar('Signal suppressed. Intent refined.', 'success');
            
            // Surgical local removal (Sprint 7)
            setIssues(prev => prev.filter(i => i._id !== issueId));
            updateFilters({ issueId: nextId });
            
            setConfirmSuppression(false);
            setActiveAction(null);
            setActionRationale('');
        } catch (err) {
            showSnackbar(err.response?.data?.message || 'Suppression failed', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const getSeverityStyles = (sev) => {
        switch(sev) {
            case 'HIGH': return 'bg-rose-500/10 text-rose-600 border-rose-500/20 dark:bg-rose-500/20';
            case 'MEDIUM': return 'bg-amber-500/10 text-amber-600 border-amber-500/20 dark:bg-amber-500/20';
            default: return 'bg-blue-500/10 text-blue-600 border-blue-500/20 dark:bg-blue-500/20';
        }
    };

    const getIssueReasonSnippet = (issue) => {
        const evidence = issue.evidence || {};
        switch(issue.type) {
            case 'DUPLICATE_SIGNAL': return `${evidence.duplicateSignalCount || '?'} overlap(s)`;
            case 'SUSPICIOUS_DRIFT': return `${evidence.intensityCount || '?'} signals / 48h`;
            default: return 'Review needed';
        }
    };

    const impact = selectedIssue ? getScoringImpact(selectedIssue.severity) : null;

    return (
        <PageShell>
            <PageHeader 
                title="Signal Quality Governance"
                subtitle="Review quality flags and intent integrity alerts across the intelligence layer"
                icon={ShieldAlert}
                actions={
                    <div className="flex items-center gap-3">
                        <Button onClick={() => loadIssues()} disabled={loading} variant="outline" className="rounded-xl font-bold bg-card h-10 px-4">
                            <RefreshCcw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} />
                            Sync Queue
                        </Button>
                    </div>
                }
            />

            {/* Filter Bar */}
            <div className="bg-card/50 backdrop-blur-md border border-border/50 p-4 rounded-3xl mb-8 flex flex-wrap items-center gap-6 shadow-sm">
                <div className="flex items-center gap-3 px-1 border-r border-border/50 pr-6 mr-1">
                    <div className="p-2 bg-primary/10 rounded-xl text-primary"><Filter className="w-4 h-4" /></div>
                    <span className="text-xs font-black uppercase tracking-widest text-foreground/70">Triage Scope</span>
                </div>
                <div className="flex items-center gap-4 flex-1">
                    <div className="w-48">
                        <Select value={currentSeverity} onValueChange={(val) => updateFilters({ severity: val })}>
                            <SelectTrigger className="rounded-2xl border-border/50 bg-card/50 font-bold text-xs uppercase tracking-tight">
                                <span className="opacity-50 mr-2 uppercase text-[10px] font-black">Severity:</span>
                                <SelectValue placeholder="All Severities" />
                            </SelectTrigger>
                            <SelectContent className="rounded-2xl border-border/50">
                                <SelectItem value="ALL" className="font-bold uppercase text-[10px]">All Severities</SelectItem>
                                <SelectItem value="HIGH" className="font-bold uppercase text-[10px] text-rose-500">High Risk</SelectItem>
                                <SelectItem value="MEDIUM" className="font-bold uppercase text-[10px] text-amber-500">Medium Risk</SelectItem>
                                <SelectItem value="LOW" className="font-bold uppercase text-[10px] text-blue-500">Low Risk</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="w-56">
                        <Select value={currentType} onValueChange={(val) => updateFilters({ type: val })}>
                            <SelectTrigger className="rounded-2xl border-border/50 bg-card/50 font-bold text-xs uppercase tracking-tight">
                                <span className="opacity-50 mr-2 uppercase text-[10px] font-black">Type:</span>
                                <SelectValue placeholder="All Issue Types" />
                            </SelectTrigger>
                            <SelectContent className="rounded-2xl border-border/50">
                                <SelectItem value="ALL" className="font-bold uppercase text-[10px]">All Issue Types</SelectItem>
                                <SelectItem value="DUPLICATE_SIGNAL" className="font-bold uppercase text-[10px]">Duplicate Signal</SelectItem>
                                <SelectItem value="SUSPICIOUS_DRIFT" className="font-bold uppercase text-[10px]">Suspicious Drift</SelectItem>
                                <SelectItem value="STALE_SIGNAL" className="font-bold uppercase text-[10px]">Stale Signal</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <div className="ml-auto flex items-center gap-2 pr-2">
                    <Badge variant="outline" className="rounded-lg font-black text-[10px] px-3 py-1 bg-muted/30 border-border/50">
                        {issues.length} Filtered Issues
                    </Badge>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* Queue */}
                <div className="lg:col-span-4 h-[750px] flex flex-col">
                    <Card className="rounded-3xl shadow-sm border-border overflow-hidden flex flex-col flex-1">
                        <CardHeader className="bg-muted/30 border-b border-border/50 py-4 px-5">
                            <div className="flex justify-between items-center">
                                <div className="flex items-center gap-2">
                                    <LayoutGrid className="w-4 h-4 text-indigo-500" />
                                    <CardTitle className="text-sm font-black uppercase tracking-widest text-foreground">Active Governance Queue</CardTitle>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="overflow-y-auto flex-1 p-3 space-y-3 custom-scrollbar">
                            {!loading && issues.length === 0 ? (
                                <EmptyState title="Queue Empty" description="No quality flags detected." icon={CheckCircle2} className="h-40" />
                            ) : (
                                issues.map(issue => (
                                    <QueueItem 
                                        key={issue._id}
                                        issue={issue}
                                        isSelected={currentIssueId === issue._id}
                                        onClick={() => handleSelectIssue(issue)}
                                        getSeverityStyles={getSeverityStyles}
                                        getIssueReasonSnippet={getIssueReasonSnippet}
                                    />
                                ))
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* workstation */}
                <div className="lg:col-span-8">
                    {detailLoading && !selectedIssue ? (
                        <div className="h-full flex flex-col items-center justify-center p-12 bg-card/30 rounded-3xl border border-dashed border-border opacity-60">
                            <RefreshCcw className="w-8 h-8 animate-spin text-primary mb-4" />
                            <p className="text-xs font-black uppercase tracking-widest">Hydrating Deep Evidence...</p>
                        </div>
                    ) : selectedIssue ? (
                        <div className="space-y-6 flex flex-col h-full animate-in fade-in slide-in-from-right-4 duration-300">
                            {detailError && (
                                <div className="bg-rose-500/10 border border-rose-500/20 p-4 rounded-2xl flex items-center gap-3 text-rose-600">
                                    <AlertTriangle className="w-4 h-4" />
                                    <p className="text-[10px] font-black uppercase tracking-widest">Partial intelligence failure. Displaying limited metadata.</p>
                                    <Button size="sm" variant="outline" className="ml-auto h-7 text-[8px] font-black" onClick={() => navigate(0)}>Restore Workspace</Button>
                                </div>
                            )}
                            <Card className="rounded-3xl border-border shadow-md overflow-hidden">
                                <CardHeader className="bg-muted/30 border-b border-border/50 py-5 px-8 flex flex-row items-center justify-between">
                                    <div className="text-left">
                                        <div className="flex items-center gap-2 mb-1">
                                            <CardTitle className="text-xl font-black tracking-tight uppercase">{selectedIssue.type.replace(/_/g, ' ')}</CardTitle>
                                            {currentIssueId && issues.length > 0 && !issues.find(i => i._id === currentIssueId) && (
                                                <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/20 text-[8px] font-black uppercase px-1.5 py-0.5 rounded-md flex items-center gap-1">
                                                    <Filter className="w-2 h-2" /> Out of Filter Scope
                                                </Badge>
                                            )}
                                        </div>
                                        <CardDescription className="text-xs font-bold text-muted-foreground uppercase tracking-widest opacity-60">Automated Quality Alert</CardDescription>
                                    </div>
                                    <Button variant="ghost" size="sm" onClick={() => handleResolve(selectedIssue._id, 'DISMISS')} className="text-xs font-black uppercase tracking-widest rounded-xl hover:text-rose-500">Dismiss</Button>
                                </CardHeader>
                                <CardContent className="p-8 space-y-8 text-left">
                                    {/* Action Rationale Workspace (Friction Mode) */}
                                    {activeAction && (
                                        <div className="p-6 bg-primary/5 border border-primary/20 rounded-3xl space-y-4 animate-in slide-in-from-top-4">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <div className="p-1 px-2 bg-primary/10 rounded-lg text-[10px] font-black uppercase tracking-widest text-primary">Operator Justification</div>
                                                    <span className="text-[10px] font-bold text-muted-foreground uppercase opacity-60">
                                                        {activeAction === 'SUPPRESS' ? 'Mandatory for system-wide weight removal' : 'Add context for the audit trail'}
                                                    </span>
                                                </div>
                                                <Button variant="ghost" size="sm" onClick={() => setActiveAction(null)} className="h-6 w-6 p-0 rounded-full"><X className="w-4 h-4" /></Button>
                                            </div>
                                            <textarea 
                                                value={actionRationale}
                                                onChange={(e) => setActionRationale(e.target.value)}
                                                placeholder={activeAction === 'SUPPRESS' ? "Explain why this signal weight should be removed (e.g. redundant source, bot activity)..." : "Add operator notes..."}
                                                className="w-full h-24 bg-background/50 border border-border/50 rounded-2xl p-4 text-xs font-bold focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all resize-none"
                                            />
                                            <div className="flex justify-end gap-3">
                                                <Button variant="ghost" size="sm" onClick={() => setActiveAction(null)} className="text-[10px] font-black uppercase tracking-widest">Cancel</Button>
                                                <Button 
                                                    onClick={() => activeAction === 'SUPPRESS' ? handleSuppress(selectedIssue.signalId._id) : handleResolve(selectedIssue._id, 'RESOLVED')}
                                                    disabled={actionLoading || (activeAction === 'SUPPRESS' && actionRationale.trim().length < 10)}
                                                    className={cn("text-[10px] font-black uppercase tracking-widest px-6 h-9 rounded-xl", activeAction === 'SUPPRESS' ? "bg-rose-600 hover:bg-rose-700" : "bg-emerald-600 hover:bg-emerald-700")}
                                                >
                                                    {actionLoading ? <RefreshCcw className="w-3 h-3 animate-spin mr-2" /> : <ShieldCheck className="w-3 h-3 mr-2" />}
                                                    {activeAction === 'SUPPRESS' ? (confirmSuppression ? 'Confirm Suppression' : 'Stage Suppression') : 'Commit Resolution'}
                                                </Button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Intelligence Banners */}
                                    {!activeAction && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-2xl p-5">
                                                <div className="flex items-center gap-3 mb-3">
                                                    <div className="p-1.5 bg-indigo-500/20 rounded-lg text-indigo-600"><Scale className="w-4 h-4" /></div>
                                                    <div className="text-[10px] font-black uppercase tracking-widest text-indigo-700 dark:text-indigo-400">Scoring Context</div>
                                                </div>
                                                <div className="space-y-3">
                                                    <div className="flex justify-between items-center bg-white/40 dark:bg-black/20 p-2 rounded-xl">
                                                        <span className="text-[11px] font-bold text-muted-foreground uppercase opacity-70">Impact Weight</span>
                                                        <span className={cn("text-xs font-black", impact?.color)}>{impact?.weight} Intent</span>
                                                    </div>
                                                    <div className="flex items-start gap-2">
                                                        <Info className="w-3 h-3 text-indigo-500 shrink-0 mt-0.5" />
                                                        <p className="text-[10px] font-bold text-indigo-900/60 dark:text-indigo-300/60 leading-tight">
                                                            External signals are capped at 60 points per account to avoid intent inflation from noisy sources.
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-5">
                                                <div className="flex items-center gap-3 mb-3">
                                                    <div className="p-1.5 bg-amber-500/20 rounded-lg text-amber-600"><Layers className="w-4 h-4" /></div>
                                                    <div className="text-[10px] font-black uppercase tracking-widest text-amber-700 dark:text-amber-400">Governance Reason</div>
                                                </div>
                                                <p className="text-xs font-bold text-amber-900/80 dark:text-amber-300/80 leading-relaxed">
                                                    {selectedIssue.type === 'DUPLICATE_SIGNAL' 
                                                        ? `Overlapping source data detected within a ${selectedIssue.evidence?.lookbackDays || 7}-day window. Operators should review if this signal provides unique intelligence.`
                                                        : `High-frequency stacking detected (${selectedIssue.evidence?.intensityCount || '3+'} signals in 48h). This pattern often indicates bot-driven drift or un-governed data ingestion errors.`
                                                    }
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Evidence & Analysis Section */}
                                    <div className="space-y-4">
                                        <div className="flex items-center gap-2 px-1">
                                            <History className="w-4 h-4 text-primary" />
                                            <span className="text-xs font-black uppercase tracking-widest text-foreground">Intelligence & Evidence</span>
                                        </div>
                                        <div className="bg-muted/30 border border-border/50 rounded-2xl p-6 space-y-6">
                                            {selectedIssue.evidence ? (
                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                                    <div className="space-y-1">
                                                        <div className="text-[10px] font-black text-muted-foreground uppercase tracking-widest opacity-40">Frequency</div>
                                                        <div className="text-lg font-black text-foreground">{selectedIssue.evidence.duplicateSignalCount || selectedIssue.evidence.intensityCount || '?'} Signals</div>
                                                    </div>
                                                    <div className="space-y-1">
                                                        <div className="text-[10px] font-black text-muted-foreground uppercase tracking-widest opacity-40">Decision Window</div>
                                                        <div className="text-lg font-black text-foreground">{selectedIssue.evidence.lookbackDays || (selectedIssue.evidence.windowHours ? `${selectedIssue.evidence.windowHours}h` : '7d')}</div>
                                                    </div>
                                                    <div className="space-y-1">
                                                        <div className="text-[10px] font-black text-muted-foreground uppercase tracking-widest opacity-40">Confidence</div>
                                                        <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 font-black uppercase tracking-widest text-[9px] rounded-lg px-2">High Evidence</Badge>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="p-4 bg-muted/20 border border-dashed border-border rounded-xl text-center opacity-40">
                                                    <p className="text-[10px] font-black uppercase tracking-widest">Awaiting Deep Evidence Analysis</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        <Card className={cn("border-border/50 shadow-sm rounded-3xl transition-all", activeAction ? "bg-muted/5 opacity-50" : "bg-muted/10 hover:bg-muted/20")}>
                                            <CardHeader className="py-4 px-6 border-b border-border/50 bg-muted/20 flex flex-row items-center justify-between">
                                                <CardTitle className="text-[10px] font-black uppercase tracking-widest text-muted-foreground flex items-center gap-2"><Building2 className="w-4 h-4 text-primary" /> Target Account</CardTitle>
                                                {!activeAction && selectedIssue.companyMasterId && <Button variant="ghost" className="h-6 px-2 text-[8px] font-black uppercase text-primary" onClick={() => navigate(`/crm/companies?companyId=${selectedIssue.companyMasterId._id}&search=${selectedIssue.companyMasterId.name}`)}>Open CRM <ExternalLink className="w-2.5 h-2.5 ml-1" /></Button>}
                                            </CardHeader>
                                            <CardContent className="p-6 space-y-2">
                                                <div className="font-black text-foreground uppercase tracking-tight truncate">{selectedIssue.companyMasterId?.name || 'Loading Account Info...'}</div>
                                                <div className="text-xs font-bold text-primary truncate">{selectedIssue.companyMasterId?.domain || 'N/A'}</div>
                                            </CardContent>
                                        </Card>

                                        <Card className={cn("border-border/50 shadow-sm rounded-3xl transition-all", activeAction ? "bg-muted/5 opacity-50" : "bg-muted/10 hover:bg-muted/20")}>
                                            <CardHeader className="py-4 px-6 border-b border-border/50 bg-muted/20 flex flex-row items-center justify-between">
                                                <CardTitle className="text-[10px] font-black uppercase tracking-widest text-muted-foreground flex items-center gap-2"><Signal className="w-4 h-4 text-indigo-500" /> Source Signal</CardTitle>
                                            </CardHeader>
                                            <CardContent className="p-6 space-y-2">
                                                {selectedIssue.signalId ? (
                                                    <>
                                                        <div className="font-black text-foreground uppercase tracking-tight truncate">{selectedIssue.signalId.label || 'Loading Signal Info...'}</div>
                                                        <Badge variant="secondary" className="text-[9px] font-bold px-2 rounded-md">{selectedIssue.signalId.source}</Badge>
                                                    </>
                                                ) : <div className="text-[10px] font-black uppercase text-muted-foreground opacity-40">Multiple signals contributing</div>}
                                            </CardContent>
                                        </Card>
                                    </div>

                                    {/* Action Footnote */}
                                    {!activeAction && (
                                        <div className="pt-8 border-t border-border mt-auto">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div className="relative">
                                                    <Button 
                                                        variant="outline"
                                                        onClick={() => setActiveAction('SUPPRESS')}
                                                        disabled={actionLoading || !selectedIssue.signalId} 
                                                        className="w-full h-14 rounded-2xl font-black uppercase tracking-widest text-xs border-b-4 border-border text-muted-foreground hover:text-rose-600 hover:border-rose-500/20"
                                                    >
                                                        <EyeOff className="w-4 h-4 mr-2" /> Suppress Signal Weight
                                                    </Button>
                                                </div>
                                                <Button 
                                                    variant="default" 
                                                    onClick={() => setActiveAction('RESOLVE')}
                                                    className="h-14 rounded-2xl font-black uppercase tracking-widest text-xs bg-emerald-600 hover:bg-emerald-700 border-b-4 border-emerald-800"
                                                >
                                                    <ShieldCheck className="w-4 h-4 mr-2" /> Correct & Resolve
                                                </Button>
                                            </div>
                                            <div className="mt-4 p-3 bg-muted/30 rounded-xl flex items-center gap-2">
                                                <ShieldAlert className="w-3 h-3 text-muted-foreground" />
                                                <span className="text-[10px] font-bold text-muted-foreground uppercase">Corrective actions trigger an immediate system-wide intent recalculation.</span>
                                            </div>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                    ) : (
                        <EmptyState title="Governance Workspace" description="Select an item to begin evidence analysis and corrective action." icon={SearchX} className="h-full" />
                    )}
                </div>
            </div>
        </PageShell>
    );
};

export default SignalGovernanceDashboard;
